<script lang="ts" setup>
    import { SidebarTrigger } from '@/shared/ui/Sidebar';
    import { useUserStore } from '@/stores/user.store';
    import LanguageSelector from './LanguageSelector.vue';
    import LoginButton from './LoginButton.vue';
    import NotificationsBadge from './NotificationsBadge.vue';
    import ProfileDropdown from './ProfileDropdown.vue';
    import { useApplicationStore } from '@/stores/application.store.js';
    import Firm24Logo from '@/assets/images/logo.svg';

    defineProps<{ sidebarEnabled: boolean }>();

    const appStore = useApplicationStore();
    const userStore = useUserStore();
    const router = useRouter();
    const { t } = useI18n();

    const logo = computed(() => appStore.tenant.squareLogoUrl ?? appStore.tenant?.logoUrl ?? Firm24Logo);
    const isSquareLogo = computed(() => !!appStore.tenant.squareLogoUrl);
    const authenticated = computed(() => !!userStore.user);
    const headerText = ref('');

    router.beforeEach((location) => {
        const title = location.matched.reduce((acc, match) => {
            return (match.meta.title as string) ?? acc;
        }, '');

        headerText.value = title ? t(`PAGE_TITLES.${title}`) : '';
    });
</script>

<template>
    <div class="h-16 flex bg-background-primary items-center p-[16px] md:px-[24px] border-b border-border-subdued">
        <SidebarTrigger
            v-if="authenticated && sidebarEnabled"
            class="mr-4 md:hidden"
        />

        <RouterLink
            v-if="!authenticated || !sidebarEnabled"
            :to="{ path: '/' }"
            class="flex gap-2 items-center"
        >
            <img
                :src="logo"
                :class="{ 'h-9': !isSquareLogo, 'aspect-square size-9 rounded-lg': isSquareLogo }"
            />
            <div class="text-sm font-semibold">{{ appStore.tenant.name ?? 'Firm24' }}</div>
        </RouterLink>

        <div
            v-if="authenticated && sidebarEnabled"
            class="text-sm font-semibold"
        >
            {{ headerText }}
        </div>

        <div class="flex-1 flex items-center justify-end gap-2">
            <template v-if="authenticated">
                <NotificationsBadge />
                <ProfileDropdown />
            </template>

            <template v-if="!authenticated">
                <LanguageSelector />
                <LoginButton />
            </template>
        </div>
    </div>
</template>
